import { type ProfileDraft, ProfileModal } from "@/components/ProfileModal";
import {
  AppSidebar,
  AppTopBar,
  HIDDEN_SCREENS,
  type ScreenId,
  type ShellUser,
} from "@/components/Shell";
import { Toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { getImpersonation, getMe, setImpersonation } from "@/lib/api";
import { useIsMobile } from "@/lib/useViewport";
import { CostMaster } from "@/screens/CostMaster";
import { CostProjection } from "@/screens/CostProjection";
import { CostReport } from "@/screens/CostReport";
import { CustomerQuoteScreen } from "@/screens/CustomerQuoteScreen";
import { DashboardScreen } from "@/screens/DashboardScreen";
import { DetailScreen } from "@/screens/DetailScreen";
import { FeedbackScreen } from "@/screens/FeedbackScreen";
import { ListScreen } from "@/screens/ListScreen";
import { LoginScreen } from "@/screens/LoginScreen";
import { MasterScreen } from "@/screens/MasterScreen";
import { NewQuoteScreen } from "@/screens/NewQuoteScreen";
import { ReportsScreen } from "@/screens/ReportsScreen";
import { SuperAdminScreen } from "@/screens/SuperAdminScreen";
import { useCallback, useEffect, useRef, useState } from "react";

const TITLES: Record<
  ScreenId | "detail" | "edit" | "customerDoc",
  { title: string; crumbs?: string }
> = {
  dashboard: { title: "대시보드" },
  list: { title: "견적 리스트" },
  new: { title: "견적 등록", crumbs: "견적 관리 / 신규 견적" },
  reports: { title: "통계 · 보고" },
  master: { title: "고객사 정보" },
  cost: { title: "원가 마스터", crumbs: "고객사 정보 / 원가 마스터" },
  projection: { title: "다년 손익 추정", crumbs: "통계 · 보고 / 다년 손익" },
  report: { title: "원가 출력", crumbs: "통계 · 보고 / 원가 출력" },
  detail: { title: "견적 상세", crumbs: "견적 관리 / 견적 리스트" },
  edit: { title: "견적 수정", crumbs: "견적 관리 / 견적 수정" },
  customerDoc: { title: "제출 견적서", crumbs: "견적 관리 / 견적 상세 / 제출 견적서" },
  feedback: { title: "개선 · 이슈" },
  superadmin: { title: "슈퍼어드민" },
};

const LoadingSpinner = () => (
  <div
    style={{
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "var(--bg-base)",
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        border: "3px solid var(--bg-card)",
        borderTopColor: "var(--brand)",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

function CmsApp() {
  const { user, signOut } = useAuth();
  const [screen, setScreen] = useState<ScreenId>("dashboard");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  // 견적 복사 — 원본 id 를 들고 견적 등록 화면을 프리필 모드로 연다(새 견적으로 저장).
  const [copyId, setCopyId] = useState<string | null>(null);
  // 고객 제출용 견적서(이슈 #110) — 견적 상세에서 진입(컨텍스트). NAV 항목 아님.
  const [customerDocId, setCustomerDocId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [feedbackCreateReq, setFeedbackCreateReq] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [profile, setProfile] = useState({ position: "견적 담당", dept: "영업관리팀" });
  // 슈퍼어드민 여부 — /me 의 is_superadmin(서버 env 화이트리스트)로만 결정.
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  // displayName 변경은 auth.currentUser 를 제자리 수정해 리렌더가 없으므로 로컬로 반영.
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const isMobile = useIsMobile();
  // Mobile nav: sidebar is hidden and slides in as an off-canvas drawer.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 직무/부서는 백엔드 /me 에 저장 — 마운트 시 1회 로드(실패 시 기본값 유지).
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (!cancelled) {
          setProfile({ position: me.position, dept: me.dept });
          setIsSuperadmin(me.isSuperadmin);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  const navigate = useCallback((id: ScreenId) => {
    setDetailId(null);
    setEditId(null);
    setCopyId(null);
    setCustomerDocId(null);
    setScreen(id);
    setDrawerOpen(false);
  }, []);

  const openQuote = useCallback((id: string) => {
    setEditId(null);
    setCopyId(null);
    setCustomerDocId(null);
    setDetailId(id);
  }, []);

  // 견적 상세 → 제출 견적서. 뒤로가기는 같은 견적 상세로 복귀.
  const openCustomerDoc = useCallback((id: string) => {
    setCustomerDocId(id);
  }, []);

  // 통합 검색 — 입력 즉시 견적 리스트로 이동해 globalQuery 필터를 적용한다.
  const search = useCallback((v: string) => {
    setQuery(v);
    if (v) {
      setDetailId(null);
      setEditId(null);
      setCopyId(null);
      setCustomerDocId(null);
      setScreen("list");
    }
  }, []);

  // 임시저장(작성 중) 견적 재편집 — 견적 등록 화면을 편집 모드로 연다.
  const editQuote = useCallback((id: string) => {
    setDetailId(null);
    setCopyId(null);
    setCustomerDocId(null);
    setEditId(id);
  }, []);

  // 견적 복사 — 견적 리스트 행에서 호출. 원본을 프리필한 새 견적 작성 화면을 연다.
  const copyQuote = useCallback((id: string) => {
    setDetailId(null);
    setEditId(null);
    setCustomerDocId(null);
    setCopyId(id);
  }, []);

  const activeNav: ScreenId = detailId || editId || copyId || customerDocId ? "list" : screen;
  const head = customerDocId
    ? TITLES.customerDoc
    : detailId
      ? TITLES.detail
      : editId
        ? TITLES.edit
        : copyId
          ? TITLES.new
          : TITLES[screen];

  // 임퍼소네이션 중이면 대상 유저 신원을 사이드바에 반영(그 유저가 보는 화면 재현).
  const impersonation = getImpersonation();
  const userName =
    impersonation?.name ||
    nameOverride ||
    user?.displayName ||
    user?.email?.split("@")[0] ||
    "준원";
  const shellUser: ShellUser = {
    name: userName,
    role: impersonation ? "임퍼소네이션" : profile.position,
    dept: impersonation ? impersonation.email : profile.dept,
    initial: userName[0].toUpperCase(),
  };

  const exitImpersonation = () => {
    setImpersonation(null);
    window.location.reload();
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg-shell)" }}>
      {!isMobile && (
        <AppSidebar
          active={activeNav}
          onNavigate={navigate}
          user={shellUser}
          onProfileClick={() => setProfileOpen(true)}
          showSuperadmin={isSuperadmin}
        />
      )}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: "var(--bg-base)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {impersonation && (
          <div
            data-testid="impersonation-banner"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "8px 16px",
              background: "var(--brand)",
              color: "#ffffff",
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            <span>
              🔓 {impersonation.name} ({impersonation.email}) 계정으로 로그인됨 · 임퍼소네이션
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={exitImpersonation}
              style={{
                border: "1px solid rgba(255,255,255,0.6)",
                background: "transparent",
                color: "#ffffff",
                borderRadius: 8,
                padding: "4px 12px",
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              원래 계정으로 복귀
            </button>
          </div>
        )}
        <AppTopBar
          title={head.title}
          crumbs={head.crumbs}
          query={query}
          setQuery={search}
          onLogout={() => void signOut()}
          onMenu={isMobile ? () => setDrawerOpen(true) : undefined}
          onFeedback={HIDDEN_SCREENS.has("feedback") ? undefined : () => navigate("feedback")}
          feedbackActive={activeNav === "feedback"}
          action={
            screen === "feedback" && !detailId
              ? {
                  icon: "plus",
                  label: "개선 · 이슈 등록",
                  onClick: () => setFeedbackCreateReq((n) => n + 1),
                }
              : undefined
          }
        />
        <main style={{ flex: 1, minWidth: 0 }}>
          {customerDocId ? (
            <CustomerQuoteScreen
              key={`cq-${customerDocId}`}
              quoteId={customerDocId}
              authorName={userName}
              onToast={showToast}
              onBack={() => setCustomerDocId(null)}
            />
          ) : detailId ? (
            <DetailScreen
              quoteId={detailId}
              onToast={showToast}
              onEditQuote={editQuote}
              onCustomerDoc={openCustomerDoc}
              onBack={() => setDetailId(null)}
            />
          ) : editId ? (
            // key 로 편집/복사/신규를 구분 — 같은 위치의 NewQuoteScreen 인스턴스가 재사용되어
            // 이전 모드의 프리필 상태가 남는 것을 막는다(모드 전환 시 remount).
            <NewQuoteScreen
              key={`edit-${editId}`}
              quoteId={editId}
              onNavigate={navigate}
              onToast={showToast}
            />
          ) : copyId ? (
            <NewQuoteScreen
              key={`copy-${copyId}`}
              copyFromId={copyId}
              onNavigate={navigate}
              onToast={showToast}
            />
          ) : screen === "dashboard" ? (
            <DashboardScreen onOpenQuote={openQuote} onNavigate={navigate} />
          ) : screen === "list" ? (
            <ListScreen
              onOpenQuote={openQuote}
              onEditQuote={editQuote}
              onCopyQuote={copyQuote}
              onNavigate={navigate}
              globalQuery={query}
            />
          ) : screen === "new" ? (
            <NewQuoteScreen key="new" onNavigate={navigate} onToast={showToast} />
          ) : screen === "reports" ? (
            <ReportsScreen onToast={showToast} />
          ) : screen === "cost" ? (
            <CostMaster onToast={showToast} />
          ) : screen === "projection" ? (
            <CostProjection onToast={showToast} />
          ) : screen === "report" ? (
            <CostReport onToast={showToast} />
          ) : screen === "feedback" ? (
            <FeedbackScreen onToast={showToast} createRequest={feedbackCreateReq} />
          ) : screen === "superadmin" ? (
            <SuperAdminScreen onToast={showToast} />
          ) : (
            <MasterScreen onToast={showToast} />
          )}
        </main>
      </div>
      {isMobile && drawerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
          <button
            type="button"
            aria-label="메뉴 닫기"
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              border: 0,
              padding: 0,
              background: "rgba(0,0,0,0.55)",
              cursor: "pointer",
              animation: "fadeIn 160ms ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              boxShadow: "rgba(0,0,0,0.4) 0 0 40px",
              animation: "slideIn 200ms cubic-bezier(.3,0,0,1)",
            }}
          >
            <AppSidebar
              active={activeNav}
              onNavigate={navigate}
              user={shellUser}
              onProfileClick={() => {
                setDrawerOpen(false);
                setProfileOpen(true);
              }}
              showSuperadmin={isSuperadmin}
              style={{ height: "100dvh", overflowY: "auto" }}
            />
          </div>
          <style>
            {
              "@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}"
            }
          </style>
        </div>
      )}
      {profileOpen && (
        <ProfileModal
          initial={{ name: userName, position: profile.position, dept: profile.dept }}
          onClose={() => setProfileOpen(false)}
          onSaved={(next: ProfileDraft) => {
            setProfile({ position: next.position, dept: next.dept });
            setNameOverride(next.name);
          }}
          onToast={showToast}
        />
      )}
      <Toast message={toast} />
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  // 임퍼소네이션 토큰이 있으면 Firebase 로그인 없이도 진입 — 서버가 매 요청 토큰을
  // 검증하므로 안전하며, LLM+Playwright 가 키로 발급한 토큰만으로 앱을 구동할 수 있다.
  if (!isAuthenticated && !getImpersonation()) return <LoginScreen />;
  return <CmsApp />;
}
